import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthProvider';
import { useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { EscopoUsuarioMaestro, NivelAcessoMaestro, UsuarioMaestro } from '../models/scope';
import { dispararAcaoSimulada } from '../utils/acaoSimulada';
import { LABEL_AREA, LABEL_NIVEL_ACESSO_MAESTRO } from '../utils/labels';

interface ModalGerenciarEscopoUsuariosProps {
  aberto: boolean;
  onFechar: () => void;
}

const NIVEIS: NivelAcessoMaestro[] = ['CONSULTA', 'OPERACAO', 'GESTAO'];

// MAESTRO-RN-20 — hoje só Perfil == "Master" enxerga tudo automaticamente no Maestro (deny by
// default); qualquer outro perfil precisa de uma linha em MaestroUserScope (empresa + nível) para
// ver qualquer coisa. Sem esta tela, isso só existia via INSERT manual no banco — bloqueando todo
// cliente novo até alguém liberar escopo manualmente. Só Master pode consultar/conceder/revogar
// escopo de outro usuário (ver MaestroUserScopeService.GetByUsuarioAlvoAsync no backend).
const ModalGerenciarEscopoUsuarios: React.FC<ModalGerenciarEscopoUsuariosProps> = ({ aberto, onFechar }) => {
  const { usuario } = useAuth();
  const { empresasEscopo, areas } = useMaestroContext();

  const [usuariosPlataforma, setUsuariosPlataforma] = useState<UsuarioMaestro[]>([]);
  const [carregandoUsuarios, setCarregandoUsuarios] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [usuarioAlvoId, setUsuarioAlvoId] = useState(0);
  const [escopos, setEscopos] = useState<EscopoUsuarioMaestro[]>([]);
  const [carregandoEscopos, setCarregandoEscopos] = useState(false);

  const [empresaIdNovo, setEmpresaIdNovo] = useState(0);
  const [areaIdNovo, setAreaIdNovo] = useState(0);
  const [nivelNovo, setNivelNovo] = useState<NivelAcessoMaestro>('CONSULTA');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setErro(null);
    setUsuarioAlvoId(0);
    setEscopos([]);
    setCarregandoUsuarios(true);
    (usuario?.clienteID ? maestroService.listarUsuariosPlataforma(usuario.clienteID) : Promise.resolve([]))
      .then(setUsuariosPlataforma)
      .catch((e: any) => setErro(e?.message ?? 'Não foi possível carregar os usuários da plataforma.'))
      .finally(() => setCarregandoUsuarios(false));
  }, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps

  const usuarioSelecionado = usuariosPlataforma.find(u => u.id === usuarioAlvoId);
  // `cargo` carrega o Perfil real do usuário na plataforma (ver adaptUsuario em maestroService.ts) —
  // diferente de `perfil`, que nesse picker é sempre um valor neutro (não é o perfil do Maestro).
  const ehUsuarioMaster = usuarioSelecionado?.cargo === 'Master';

  const carregarEscopos = (id: number) => {
    setCarregandoEscopos(true);
    maestroService.listarEscopoUsuario(id)
      .then(setEscopos)
      .catch((e: any) => { setEscopos([]); dispararAcaoSimulada(e?.response?.data?.message ?? e?.message ?? 'Não foi possível carregar o escopo do usuário.'); })
      .finally(() => setCarregandoEscopos(false));
  };

  useEffect(() => {
    if (!usuarioAlvoId || ehUsuarioMaster) { setEscopos([]); return; }
    carregarEscopos(usuarioAlvoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarioAlvoId, ehUsuarioMaster]);

  const conceder = () => {
    if (!usuarioAlvoId || !empresaIdNovo) return;
    setEnviando(true);
    maestroService.concederEscopo({ usuarioId: usuarioAlvoId, empresaId: empresaIdNovo, areaId: areaIdNovo || undefined, nivel: nivelNovo })
      .then(() => { setEmpresaIdNovo(0); setAreaIdNovo(0); setNivelNovo('CONSULTA'); carregarEscopos(usuarioAlvoId); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível conceder o escopo.'))
      .finally(() => setEnviando(false));
  };

  const revogar = (escopo: EscopoUsuarioMaestro) => {
    if (!window.confirm('Revogar este escopo de acesso? A ação é definitiva — o usuário perde o acesso a essa empresa imediatamente.')) return;
    maestroService.revogarEscopo(escopo.id)
      .then(() => carregarEscopos(usuarioAlvoId))
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível revogar o escopo.'));
  };

  const nomeEmpresa = (id: number): string => empresasEscopo.find(e => e.id === id)?.nome ?? `Empresa #${id}`;
  const nomeArea = (id?: number): string => {
    if (!id) return 'Todas as áreas';
    const area = areas.find(a => a.id === id);
    return area ? (LABEL_AREA[area.tipo] ?? area.nome) : `Área #${id}`;
  };

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth='md' fullWidth PaperProps={{ className: 'mc-dialog' }}>
      <DialogTitle>Escopo de Acesso dos Usuários</DialogTitle>
      <DialogContent>
        <p className='mc-texto-neutro' style={{ marginTop: 0 }}>
          Só usuários com perfil Master enxergam tudo automaticamente. Qualquer outro perfil precisa de um escopo explícito (empresa + nível) aqui para acessar dados do Maestro Contábil.
        </p>

        {erro && <p className='mc-texto-danger'>{erro}</p>}

        <div className='mc-form-inline' style={{ marginBottom: 12 }}>
          <label>Usuário
            <select value={usuarioAlvoId} onChange={e => setUsuarioAlvoId(Number(e.target.value))} disabled={carregandoUsuarios}>
              <option value={0}>Selecione...</option>
              {usuariosPlataforma.map(u => <option key={u.id} value={u.id}>{u.nome}{u.cargo && u.cargo !== '—' ? ` (${u.cargo})` : ''}</option>)}
            </select>
          </label>
        </div>

        {!usuarioAlvoId ? (
          <p className='mc-texto-neutro'>Selecione um usuário para ver e gerenciar o escopo de acesso dele.</p>
        ) : ehUsuarioMaster ? (
          <p className='mc-texto-neutro'><i className='bi bi-shield-check' /> Master tem acesso irrestrito por padrão — não precisa de escopo cadastrado.</p>
        ) : (
          <>
            <div className='mc-form-inline'>
              <label>Empresa
                <select value={empresaIdNovo} onChange={e => setEmpresaIdNovo(Number(e.target.value))}>
                  <option value={0}>Selecione...</option>
                  {empresasEscopo.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                </select>
              </label>
              <label>Área (opcional)
                <select value={areaIdNovo} onChange={e => setAreaIdNovo(Number(e.target.value))}>
                  <option value={0}>Todas as áreas da empresa</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{LABEL_AREA[a.tipo]}</option>)}
                </select>
              </label>
              <label>Nível
                <select value={nivelNovo} onChange={e => setNivelNovo(e.target.value as NivelAcessoMaestro)}>
                  {NIVEIS.map(n => <option key={n} value={n}>{LABEL_NIVEL_ACESSO_MAESTRO[n]}</option>)}
                </select>
              </label>
              <button type='button' className='mc-btn mc-btn--primary' disabled={enviando || !empresaIdNovo} onClick={conceder}>
                {enviando ? 'Concedendo...' : 'Conceder escopo'}
              </button>
            </div>

            {carregandoEscopos
              ? <p className='mc-texto-neutro'>Carregando...</p>
              : escopos.length === 0
                ? <p className='mc-texto-neutro'>Nenhum escopo cadastrado para este usuário ainda — sem escopo, ele não enxerga nenhuma empresa no Maestro.</p>
                : (
                  <div className='mc-membros-lista'>
                    {escopos.map(esc => (
                      <div key={esc.id} className='mc-membro-card' style={{ opacity: esc.ativo ? 1 : .5 }}>
                        <div className='mc-membro-card__nome'>{nomeEmpresa(esc.empresaId)}</div>
                        <div className='mc-membro-card__email'>{nomeArea(esc.areaId)}</div>
                        <span className='mc-badge mc-badge--info'>{LABEL_NIVEL_ACESSO_MAESTRO[esc.nivel]}</span>
                        <span className={`mc-badge mc-badge--${esc.ativo ? 'success' : 'neutro'}`}>{esc.ativo ? 'Ativo' : 'Revogado'}</span>
                        {esc.ativo
                          ? <button type='button' className='mc-btn mc-btn--sm mc-btn--secondary' onClick={() => revogar(esc)}>Revogar</button>
                          : <span />}
                      </div>
                    ))}
                  </div>
                )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalGerenciarEscopoUsuarios;
